"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISSED_KEY = "erikonomie:a2hs-dismissed";
const SHOW_DELAY_MS = 4000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !("MSStream" in window);
}

/**
 * Aktiver "Zum Home-Bildschirm hinzufügen"-Hinweis-Banner (backlog.md -> Aktienfinder-
 * Benchmark, "PWA-Installierbarkeit existiert bereits, übrig bleibt nur ein aktiver
 * Hinweis-Banner statt des passiven Browser-Standard-Prompts") - nur mobil (`md:hidden`,
 * gleiche Konvention wie sonst site-weit für mobile-only UI), nicht wenn bereits als App
 * installiert/gestartet, nicht erneut nach einmaligem Wegklicken (localStorage). Android/
 * Chrome nutzt das echte `beforeinstallprompt`-Event, iOS Safari kennt dieses Event nicht
 * und bekommt stattdessen eine kurze Bedienungs-Anleitung (kein programmatischer Trigger
 * dort verfügbar).
 */
export function AddToHomescreenBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (isStandalone()) return;

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    // Alle setState-Aufrufe gebuendelt im (async) Timeout-Callback statt synchron im
    // Effect-Body (react-hooks/set-state-in-effect) - erscheint erst nach kurzer
    // Verzoegerung, damit der Banner nicht sofort beim ersten Seitenaufbau aufblitzt.
    const timeout = setTimeout(() => {
      setDismissed(window.localStorage.getItem(DISMISSED_KEY) === "1");
      setShowIosHint(isIos());
      setVisible(true);
    }, SHOW_DELAY_MS);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      clearTimeout(timeout);
    };
  }, []);

  function dismiss() {
    setVisible(false);
    setDismissed(true);
    window.localStorage.setItem(DISMISSED_KEY, "1");
  }

  async function handleInstall() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  }

  const canShow = visible && !dismissed && (deferredPrompt !== null || showIosHint);
  if (!canShow) return null;

  return (
    <div
      role="dialog"
      aria-label="Erikonomie zum Home-Bildschirm hinzufügen"
      // bottom-24 statt bottom-3: laesst auf mobilen Seiten, auf denen zusaetzlich der
      // "Zurueck nach oben"-Button (BackToTopButton, fixed bottom-6 right-4) sichtbar
      // ist, genug vertikalen Abstand, um eine Ueberlappung der beiden fixed-Elemente
      // in der unteren rechten Ecke zu vermeiden.
      className="fixed inset-x-3 bottom-24 z-40 flex items-center gap-3 rounded-xl border border-hairline bg-card p-3 shadow-lg md:hidden"
    >
      <Image src="/icons/icon-192.png" alt="" width={40} height={40} className="shrink-0 rounded-lg" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">Erikonomie zum Home-Bildschirm</p>
        <p className="mt-0.5 text-xs text-ink-secondary">
          {deferredPrompt
            ? "Schneller Zugriff, wie eine echte App."
            : "Teilen-Symbol antippen, dann „Zum Home-Bildschirm“."}
        </p>
      </div>
      {deferredPrompt && (
        <Button type="button" size="sm" onClick={handleInstall} className="shrink-0">
          Hinzufügen
        </Button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Hinweis schließen"
        className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-muted"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
